import { computed } from 'vue';
import { useSessionStore } from '@/store';
import { mapDeliveryWindowLabel } from '@/utilities/deliveryDateHelpers';

/**
 * Composable that returns a computed delivery date display string.
 *
 * Prefers the backend-provided `customer_delivery_label` when available,
 * which already contains customer-friendly text. Falls back to mapping
 * the raw `dates.delivery.formatted` value for backward compatibility.
 */
export function useDeliveryDateDisplay() {
    const sessionStore = useSessionStore();

    // Append a single-day delivery time (e.g. "before midday") to a concrete date label.
    const withTime = (label: string, time?: { formatted?: string | null } | null): string =>
        time?.formatted ? `${label} (${time.formatted})` : label;

    const deliveryDateDisplay = computed(() => {
        const selectedMatch = sessionStore.getPrimaryProduct?.selectedMatch;
        const job = sessionStore.getJob;
        const isInterstateMove = sessionStore.isInterstate;

        // Prefer the backend-provided customer-friendly label when available
        if (sessionStore.hasMoveBeenAccepted && job) {
            if (job.customer_delivery_label) {
                return job.customer_delivery_label;
            }

            if (job.dates?.delivery?.formatted) {
                const label =
                    isInterstateMove ?
                        mapDeliveryWindowLabel(job.dates.delivery.formatted)
                    :   job.dates.delivery.formatted;
                return withTime(label, job.dates.delivery.time);
            }

            return null;
        }

        if (selectedMatch?.customer_delivery_label) {
            return selectedMatch.customer_delivery_label;
        }

        const formatted = selectedMatch?.dates?.delivery?.formatted ?? null;
        if (!formatted) {
            return null;
        }

        const label = isInterstateMove ? mapDeliveryWindowLabel(formatted) : formatted;
        return withTime(label, selectedMatch?.dates?.delivery?.time);
    });

    return { deliveryDateDisplay };
}
